import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { HistoryEntityLink } from "../../../ui/components/ListView/logSummaryLinks";
import type {
  CursorPage,
  CursorPageArgs,
} from "../../../ui/components/Table/hooks/useInfiniteListQuery";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchUnifiedHistoryFeed,
  type HistoryEvent,
  type HistorySource,
} from "../../lib/api/history";
import { createHistoryTableColumns } from "./historyTableColumns";
import {
  createOrganizationEntityFilter,
  createProductEntityFilter,
  createUserEntityFilter,
} from "./historyEntityFilters";
import * as pageStyles from "../users/UsersListPage.css";

export function HistoryListPage() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const [degraded, setDegraded] = React.useState<HistorySource[]>([]);

  const token =
    accessToken ??
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("access_token")
      : null);

  const columns = React.useMemo(() => {
    if (!token) return createHistoryTableColumns(t);
    return createHistoryTableColumns(t, {
      user: createUserEntityFilter(token),
      organization: createOrganizationEntityFilter(token),
      product: createProductEntityFilter(token),
    });
  }, [t, token]);

  const fetchPage = React.useCallback(
    async (args: CursorPageArgs): Promise<CursorPage<HistoryEvent>> => {
      if (!token) return { data: [], nextCursor: null, hasMore: false };
      const page = await fetchUnifiedHistoryFeed(token, {
        limit: args.limit,
        cursor: args.cursor,
        search: args.search,
        order: args.order,
        filters: args.filters,
      });
      setDegraded(page.degraded ?? []);
      return {
        data: page.data,
        nextCursor: page.nextCursor,
        hasMore: page.hasMore,
      };
    },
    [token],
  );

  const onLogEntityClick = React.useCallback(
    (link: HistoryEntityLink) => {
      if (link.kind === "user") {
        void navigate({
          to: "/workspace/users/$userId",
          params: { userId: link.id },
        });
        return;
      }
      if (link.kind === "organization") {
        void navigate({
          to: "/workspace/organizations/$orgId",
          params: { orgId: link.id },
        });
        return;
      }
      if (link.kind === "product") {
        void navigate({
          to: "/workspace/products/$productId",
          params: { productId: link.id },
        });
        return;
      }
      if (link.kind === "certification") {
        void navigate({
          to: "/system/certifications/$moduleId",
          params: { moduleId: link.id },
        });
      }
    },
    [navigate],
  );

  const cursorFeed = React.useMemo(() => ({ fetchPage }), [fetchPage]);

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.system.history.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.history.title")}
        </Text>
      </div>

      {degraded.length > 0 && (
        <div className={pageStyles.errorWrap}>
          <Callout variant="info">
            {t("pro.system.history.degraded", {
              sources: degraded
                .map((s) =>
                  s === "user-service"
                    ? t("pro.system.history.tab.userService")
                    : t("pro.system.history.tab.productService"),
                )
                .join(", "),
            })}
          </Callout>
        </div>
      )}

      <div className={pageStyles.tableHost}>
        <ListView<HistoryEvent>
          listId="system-history-unified"
          userId={user?.id}
          columns={columns}
          fetchList={async () => ({ data: [], total: 0 })}
          cursorFeed={cursorFeed}
          enabledViews={["log"]}
          defaultSort={{ sort_by: "occurredAt", order: "desc" }}
          defaultPageSize={50}
          searchPlaceholder={t("pro.system.history.search")}
          emptyTitle={t("pro.system.history.emptyTitle")}
          emptySubtitle={t("pro.system.history.emptySubtitle")}
          timeRangeFilter={{ enabled: true }}
          onLogEntityClick={onLogEntityClick}
          fillContainer
        />
      </div>
    </div>
  );
}
